'use client';

import CalculatorResults from '@/components/blocks/FindYourPower/CalculatorResults';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Slider } from '@/components/ui/slider';
import debug from '@/utils/debug';
import { useState } from 'react';

const appendPercentage = value => (value === 0 || value ? `${value}%` : 'N/A');

const formatPrice = (price, max, label) => {
  return price === max ? `${price.toLocaleString()} ${label}` : `${price.toLocaleString()} ${label}`;
};

function PriceRanger({ label, value, onChange, min, max }) {
  return (
    <div className='price-ranger flex flex-col gap-3'>
      <Label className='tabular-nums text-primary'>
        <span className='h3'>{formatPrice(value[0], max, label)}</span>
      </Label>
      <div className='slider-wrapper flex flex-col gap-4'>
        <Slider value={value} onValueChange={onChange} min={min} max={max} aria-label='Price range slider' />
        <div className='flex gap-4 justify-between'>
          <span className='min'>
            {min} {label}
          </span>
          <span className='max'>
            {max.toLocaleString()} {label}
          </span>
        </div>
      </div>
    </div>
  );
}

function LockPeriods({ vaultsTimeLocks = [], labels = {}, onChange, value }) {
  if (!vaultsTimeLocks?.length) return <></>;

  const { months, flexible } = labels;

  const format = lock => ({
    ...lock,
    label: lock?.min_time_lock_months === 0 ? flexible : `${lock?.min_time_lock_months} ${months}`
  });

  return (
    <fieldset className='flex flex-col gap-2'>
      <RadioGroup
        className='lock-periods grid grid-cols-3 xs:grid-cols-4 sm:grid-cols-5 lg:grid-cols-4 gap-2'
        defaultValue={value}
        onValueChange={onChange}
      >
        {vaultsTimeLocks.map(format).map((item, index) => (
          <label
            key={index}
            className='relative flex cursor-pointer flex-col items-center gap-3 rounded-lg border border-white/10 px-2 py-3 text-center ring-offset-primary/5 transition-colors has-[[data-disabled]]:cursor-not-allowed has-[[data-state=checked]]:border-primary/20 bg-white/5 has-[[data-state=checked]]:bg-primary/40 has-[[data-disabled]]:opacity-50 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring/70 has-[:focus-visible]:ring-offset-2 flex-1'
          >
            <RadioGroupItem
              id={item.l_value}
              value={item.l_value}
              className='sr-only after:absolute after:inset-0'
              aria-label={item.label}
            />
            <p className='label font-medium leading-none text-foreground text-nowrap'>{item.label}</p>
          </label>
        ))}
      </RadioGroup>
    </fieldset>
  );
}

export default function OUIXPowerCalculator({ title = {}, labels, footer, vaultsTimeLocks = [], tiers = [], notice }) {
  const {
    input_the_amount_of_ouix_you_staked,
    choose_your_staking_period,
    projected_monthly_trading_volume,
    your_ouixpower,
    ouix,
    usd,
    spot_trading_fees,
    yield_boost,
    referral_commission,
    ouix_discount
  } = labels;

  const min_price = 0;
  const max_price = 300_000;
  const max_volume = 100_000_0000;

  const [amount, setAmount] = useState([min_price, max_price]);
  const [volume, setVolume] = useState([min_price, max_volume]);
  const [lockValue, setLockValue] = useState(vaultsTimeLocks?.[0]?.l_value);

  const { label, value } = title;

  const powerValue = amount[0] * lockValue + (volume[0] * 2) / 100;

  let matchedTier = null;
  tiers.forEach((tier, index) => {
    if (tier?.required_power <= powerValue && tier?.minimum_tokens <= amount[0]) {
      matchedTier = {
        ...tier,
        level: index + 1
      };
    }
  });

  return (
    <div className='ouix-power-calculator'>
      <p className='flex items-center gap-x-3 pl-4 sm:pl-8 font-light pb-2'>
        <span className='label text-primary'>{label}</span> {value}
      </p>
      <div
        className='flex flex-col gap-5 border border-primary/10 rounded-xl p-4 sm:p-8 lg:max-w-lg w-full backdrop-blur min-w-fit md:min-w-[32rem]'
        style={{
          backgroundImage: 'linear-gradient(180deg, hsla(var(--primary)/0.05) 0%, hsla(var(--primary)/0.20) 100%)'
        }}
      >
        <p className='font-semibold'>1. {input_the_amount_of_ouix_you_staked}:</p>
        <PriceRanger label={ouix} value={amount} onChange={setAmount} min={min_price} max={max_price} />

        <p className='font-semibold'>2. {choose_your_staking_period}:</p>
        <LockPeriods vaultsTimeLocks={vaultsTimeLocks} labels={labels} value={lockValue} onChange={setLockValue} />

        <p className='font-semibold'>3. {projected_monthly_trading_volume}:</p>
        <PriceRanger label={usd} value={volume} onChange={setVolume} min={min_price} max={max_volume} />

        <div className='overflow-clip flex-1 max-w-full'>
          <CalculatorResults
            title={your_ouixpower}
            subtitle={
              matchedTier ? `${Math.floor(powerValue)} = ${labels.level} ${matchedTier?.level ?? 'N/A'}` : 'N/A'
            }
            list={[
              {
                label: spot_trading_fees,
                value: appendPercentage(matchedTier?.base_trading_progressive_fee) ?? 'N/A'
              },
              { label: yield_boost, value: appendPercentage(matchedTier?.yield_boost) ?? 'N/A' },
              {
                label: referral_commission,
                value: appendPercentage(matchedTier?.referral_commission) ?? 'N/A'
              },
              { label: ouix_discount, value: appendPercentage(matchedTier?.token_cashback) ?? 'N/A' }
            ]}
          />
        </div>

        {footer && (
          <div className='footer link-primary text-center prose' dangerouslySetInnerHTML={{ __html: footer }} />
        )}

        {notice && <p className='text-center text-muted text-[10px] text-zinc-400 leading-normal'>{notice}</p>}
      </div>
    </div>
  );
}
